/**
 * AddWorkshopParticipantsModal — MED-566 (E18-9), MED-959
 * Add participants to a workshop via All / By Group / Individual / Upload selection.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Radio,
  Checkbox,
  Loader,
  Center,
  Alert,
  ScrollArea,
  Badge,
  Avatar,
  Divider,
  TextInput,
  Textarea,
} from '@mantine/core';
import { AlertCircle, Search, Upload, CheckCircle, AlertTriangle } from 'react-feather';
import { workshopsApi, type WorkshopEligibleParticipant } from '../../../../services/api/workshops';

type AddMode = 'all' | 'by_group' | 'individual' | 'upload';

/** MED-959: Parse a raw string of emails into a deduplicated, trimmed, lowercase array. */
function parseEmails(raw: string): string[] {
  const seen = new Set<string>();
  return raw
    .split(/[\n,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0 && !seen.has(s) && seen.add(s));
}

interface UploadPreview {
  matched: WorkshopEligibleParticipant[];
  unmatched: string[];
}

interface AddWorkshopParticipantsModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  workshopId: string;
  workshopTitle: string;
  onAdded: () => void;
}

export function AddWorkshopParticipantsModal({
  opened,
  onClose,
  cohortId,
  workshopId,
  workshopTitle,
  onAdded,
}: AddWorkshopParticipantsModalProps) {
  const [mode, setMode] = useState<AddMode>('all');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eligible, setEligible] = useState<WorkshopEligibleParticipant[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  // MED-959: Upload mode state
  const [emailInput, setEmailInput] = useState('');
  const [uploadPreview, setUploadPreview] = useState<UploadPreview | null>(null);

  const loadEligible = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await workshopsApi.listEligibleParticipants(cohortId, workshopId);
      setEligible(data);
    } catch {
      setError('Failed to load participants');
    } finally {
      setLoading(false);
    }
  }, [cohortId, workshopId]);

  useEffect(() => {
    if (opened) {
      setMode('all');
      setSelectedIds(new Set());
      setSelectedGroupIds(new Set());
      setSearch('');
      setEmailInput('');
      setUploadPreview(null);
      setError(null);
      loadEligible();
    }
  }, [opened, loadEligible]);

  // Build email→participant lookup once
  const eligibleByEmail = useMemo(
    () => new Map(eligible.map((p) => [p.email.toLowerCase(), p])),
    [eligible],
  );

  // Unique groups from eligible participants
  const groups = Array.from(
    new Map(
      eligible
        .filter((p) => p.group_id && p.group_name)
        .map((p) => [p.group_id!, { id: p.group_id!, name: p.group_name! }]),
    ).values(),
  ).sort((a, b) => a.name.localeCompare(b.name));

  const filteredEligible = eligible.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.first_name.toLowerCase().includes(q) ||
      p.last_name.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q)
    );
  });

  /** MED-959: Resolve pasted emails against eligible participants. */
  const handleProcessEmails = () => {
    const emails = parseEmails(emailInput);
    if (emails.length === 0) return;

    const matched: WorkshopEligibleParticipant[] = [];
    const unmatched: string[] = [];

    for (const email of emails) {
      const participant = eligibleByEmail.get(email);
      if (participant) {
        matched.push(participant);
      } else {
        unmatched.push(email);
      }
    }

    setUploadPreview({ matched, unmatched });
  };

  const getUserIdsToAdd = (): string[] => {
    if (mode === 'all') return eligible.map((p) => p.user_id);
    if (mode === 'by_group') {
      return eligible
        .filter((p) => p.group_id && selectedGroupIds.has(p.group_id))
        .map((p) => p.user_id);
    }
    if (mode === 'upload' && uploadPreview) {
      return uploadPreview.matched.map((p) => p.user_id);
    }
    return Array.from(selectedIds);
  };

  const handleSave = async () => {
    const userIds = getUserIdsToAdd();
    if (userIds.length === 0) return;

    setSaving(true);
    setError(null);
    try {
      await workshopsApi.addParticipants(cohortId, workshopId, userIds);
      onAdded();
      onClose();
    } catch {
      setError('Failed to add participants');
    } finally {
      setSaving(false);
    }
  };

  const countToAdd = getUserIdsToAdd().length;

  // MED-959: In upload mode with a preview, use custom footer buttons
  const showUploadPreview = mode === 'upload' && uploadPreview !== null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} size="sm">
          Add Participants — {workshopTitle}
        </Text>
      }
      size="md"
    >
      <Stack gap="md">
        {error && (
          <Alert icon={<AlertCircle size={16} />} color="red" variant="light">
            {error}
          </Alert>
        )}

        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : eligible.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            All cohort participants are already assigned to this workshop.
          </Text>
        ) : (
          <>
            <Radio.Group
              value={mode}
              onChange={(v) => {
                setMode(v as AddMode);
                setUploadPreview(null);
              }}
              label="Add participants by"
            >
              <Stack gap="xs" mt="xs">
                <Radio value="all" label={`All eligible participants (${eligible.length})`} />
                {groups.length > 0 && <Radio value="by_group" label="By Group" />}
                <Radio value="individual" label="Individual selection" />
                <Radio value="upload" label="Upload list" />
              </Stack>
            </Radio.Group>

            {mode === 'by_group' && (
              <Stack gap="xs">
                <Text size="sm" fw={500}>Select groups</Text>
                {groups.map((g) => {
                  const groupCount = eligible.filter((p) => p.group_id === g.id).length;
                  return (
                    <Checkbox
                      key={g.id}
                      label={
                        <Group gap="xs">
                          <Text size="sm">{g.name}</Text>
                          <Badge size="xs" variant="light" color="gray">
                            {groupCount}
                          </Badge>
                        </Group>
                      }
                      checked={selectedGroupIds.has(g.id)}
                      onChange={(e) => {
                        const next = new Set(selectedGroupIds);
                        if (e.currentTarget.checked) next.add(g.id);
                        else next.delete(g.id);
                        setSelectedGroupIds(next);
                      }}
                    />
                  );
                })}
              </Stack>
            )}

            {mode === 'individual' && (
              <Stack gap="xs">
                <TextInput
                  placeholder="Search participants..."
                  leftSection={<Search size={14} />}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  size="sm"
                />
                <ScrollArea h={240}>
                  <Stack gap={4}>
                    {filteredEligible.map((p) => (
                      <Checkbox
                        key={p.user_id}
                        label={
                          <Group gap="xs">
                            <Avatar size={24} radius="xl" color="blue">
                              {p.first_name.charAt(0)}{p.last_name.charAt(0)}
                            </Avatar>
                            <div>
                              <Text size="sm">{p.first_name} {p.last_name}</Text>
                              {p.group_name && (
                                <Text size="xs" c="dimmed">{p.group_name}</Text>
                              )}
                            </div>
                          </Group>
                        }
                        checked={selectedIds.has(p.user_id)}
                        onChange={(e) => {
                          const next = new Set(selectedIds);
                          if (e.currentTarget.checked) next.add(p.user_id);
                          else next.delete(p.user_id);
                          setSelectedIds(next);
                        }}
                      />
                    ))}
                    {filteredEligible.length === 0 && (
                      <Text size="sm" c="dimmed" ta="center" py="md">No participants found</Text>
                    )}
                  </Stack>
                </ScrollArea>
              </Stack>
            )}

            {/* MED-959: Upload / Paste emails mode */}
            {mode === 'upload' && !uploadPreview && (
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Paste participant email addresses below — one per line, or separated by commas.
                </Text>
                <Textarea
                  placeholder={'jane.doe@company.com\nbob.smith@company.com\n...'}
                  minRows={6}
                  maxRows={10}
                  autosize
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.currentTarget.value)}
                />
                <Group justify="flex-end">
                  <Button
                    leftSection={<Upload size={14} />}
                    onClick={handleProcessEmails}
                    disabled={emailInput.trim().length === 0}
                    size="sm"
                  >
                    Process emails
                  </Button>
                </Group>
              </Stack>
            )}

            {/* MED-959: Upload preview */}
            {showUploadPreview && (
              <Stack gap="sm">
                {uploadPreview.matched.length > 0 && (
                  <Alert icon={<CheckCircle size={16} />} color="green" variant="light">
                    {uploadPreview.unmatched.length === 0
                      ? `All ${uploadPreview.matched.length} emails matched`
                      : `${uploadPreview.matched.length} email${uploadPreview.matched.length !== 1 ? 's' : ''} matched`}
                  </Alert>
                )}

                {uploadPreview.unmatched.length > 0 && (
                  <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
                    <Text size="sm" fw={500} mb={4}>
                      {uploadPreview.unmatched.length} email{uploadPreview.unmatched.length !== 1 ? 's' : ''} not found in this cohort:
                    </Text>
                    <ScrollArea.Autosize mah={120}>
                      <Stack gap={2}>
                        {uploadPreview.unmatched.map((email) => (
                          <Text key={email} size="xs" ff="monospace">
                            {email}
                          </Text>
                        ))}
                      </Stack>
                    </ScrollArea.Autosize>
                  </Alert>
                )}

                {uploadPreview.matched.length > 0 && (
                  <Text size="sm" c="dimmed">
                    Confirming will add {uploadPreview.matched.length} participant{uploadPreview.matched.length !== 1 ? 's' : ''} to this workshop session.
                    {uploadPreview.unmatched.length > 0 && ' Unmatched emails are ignored.'}
                  </Text>
                )}

                {uploadPreview.matched.length === 0 && (
                  <Text size="sm" c="dimmed">
                    None of the provided emails matched eligible participants in this cohort.
                    Go back to correct your list and try again.
                  </Text>
                )}
              </Stack>
            )}
          </>
        )}

        <Divider />

        <Group justify="flex-end">
          {showUploadPreview ? (
            <>
              <Button
                variant="subtle"
                color="gray"
                onClick={() => setUploadPreview(null)}
              >
                Go Back
              </Button>
              <Button
                onClick={handleSave}
                loading={saving}
                disabled={uploadPreview!.matched.length === 0}
              >
                Add {uploadPreview!.matched.length} Participant{uploadPreview!.matched.length !== 1 ? 's' : ''}
              </Button>
            </>
          ) : (
            <>
              <Button variant="subtle" color="gray" onClick={onClose}>
                Cancel
              </Button>
              {mode !== 'upload' && (
                <Button
                  onClick={handleSave}
                  loading={saving}
                  disabled={loading || countToAdd === 0}
                >
                  Add {countToAdd > 0 ? `${countToAdd} Participant${countToAdd !== 1 ? 's' : ''}` : 'Participants'}
                </Button>
              )}
            </>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}

export default AddWorkshopParticipantsModal;
